import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarOff, Building2, Users, Activity, ShieldCheck, TrendingUp, TrendingDown, Clock, ClipboardList,
  CheckCircle2, AlertCircle, BedDouble, Award, Trophy, AlertTriangle, Sparkles, Star, Calendar, Loader2
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Bar, BarChart } from "recharts";
import { AIInsightCard } from "@/components/AIChat";
import RelazioniFeedWidget from "@/components/RelazioniFeedWidget";

function AbsenceBanner() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/me/absence-today").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  if (!info?.in_absence) return null;
  const a = info.absence;
  const tipo = ({ ferie: "ferie", malattia: "malattia", permesso: "permesso", altro: "assenza" })[a.tipo] || "assenza";
  return (
    <div className="rounded-2xl border-l-4 border-l-lavender-400 bg-lavender-50/50 dark:bg-lavender-950/30 px-4 py-3 flex items-center gap-3" data-testid="absence-banner">
      <CalendarOff className="h-5 w-5 text-lavender-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">In {tipo} fino al <span className="tabular-nums">{a.data_fine}</span></div>
        <div className="text-xs text-muted-foreground">Le task camere sono sospese per questo periodo.</div>
      </div>
    </div>
  );
}

const SEM_COLOR = { verde: "text-emerald-500", giallo: "text-amber-500", rosso: "text-rose-500" };
const sem = (v, t1 = 80, t2 = 60) => v >= t1 ? "verde" : v >= t2 ? "giallo" : "rosso";

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-xl bg-muted/40 ${className}`} />;
}

function KpiCard({ icon: Icon, label, value, unit, delta, semaforo, sub }) {
  return (
    <div className="ih-glass rounded-2xl p-5 ih-card-hover">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-3xl font-medium">{value}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[11px]">
        {delta != null && (
          <span className={`flex items-center gap-0.5 ${delta >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {delta >= 0 ? "+" : ""}{delta}
          </span>
        )}
        {semaforo && <span className={`text-[10px] uppercase font-semibold ${SEM_COLOR[semaforo]}`}>{semaforo}</span>}
        {sub && <span className="text-muted-foreground truncate">{sub}</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(90);

  const isCorporate = user && ["super_admin", "capo_area"].includes(user.role);

  useEffect(() => {
    if (!user) return;
    if (!isCorporate) { setLoading(false); return; }
    setLoading(true);
    api.get(`/dashboard/corporate-summary?periodo=${periodo}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [user, isCorporate, periodo]);

  if (!user) return null;

  // Operational role: keep simple welcome
  if (!isCorporate) {
    return (
      <>
        <AbsenceBanner />
        <OperationalDashboard user={user} />
      </>
    );
  }

  return (
    <div className="space-y-6 ih-anim-in" data-testid="corporate-dashboard">
      <AbsenceBanner />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> {user.role === "super_admin" ? "Corporate" : "Cluster"} Dashboard
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">
            Benvenuto, <span className="font-medium">{user.nome}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Una vista d&apos;insieme su qualità, performance e operatività.</p>
        </div>
        <div className="flex gap-1 ih-glass rounded-full p-1">
          {[30, 60, 90, 180].map((p) => (
            <button key={p} data-testid={`dash-period-${p}`} onClick={() => setPeriodo(p)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${periodo === p ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{p}gg</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : data && (
        <>
          {/* Hero KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="hero-kpis">
            <div className="ih-glass rounded-2xl p-5 ih-card-hover" data-testid="kpi-qualita">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Qualità Corporate</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl font-medium">{data.hero.qualita}</span>
                <span className="text-xs text-muted-foreground">/100</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[11px]" title="Variazione mese su mese">
                <span className={`flex items-center gap-0.5 ${data.hero.qualita_delta >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
                  {data.hero.qualita_delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {data.hero.qualita_delta >= 0 ? "+" : ""}{data.hero.qualita_delta}
                </span>
                <span className={`text-[10px] uppercase font-semibold ${SEM_COLOR[sem(data.hero.qualita, 80, 65)]}`}>{sem(data.hero.qualita, 80, 65)}</span>
              </div>
              <div className="mt-2 pt-2 border-t border-border/40 text-[10px]" data-testid="insight-text" title="Variazione mese su mese">
                {data.hero.biggest_drop || data.hero.biggest_rise ? (
                  <div className="flex flex-col gap-0.5">
                    {data.hero.biggest_drop && <span className="text-rose-600 dark:text-rose-400">⚠️ {data.hero.biggest_drop.nome} in calo {data.hero.biggest_drop.delta} pt</span>}
                    {data.hero.biggest_rise && <span className="text-emerald-600 dark:text-emerald-400">⬆️ {data.hero.biggest_rise.nome} in crescita +{data.hero.biggest_rise.delta} pt</span>}
                  </div>
                ) : (
                  <span className="text-muted-foreground">📊 Andamento stabile</span>
                )}
              </div>
            </div>
            <KpiCard icon={Building2} label="Hotel & Ispezione" value={data.hero.hotels_count} unit="hotel" sub={`${data.hero.audit_completati_mese} ispezione/mese`} />
            <KpiCard icon={Award} label="Performance RM media" value={data.hero.performance_rm} unit="/100" semaforo={sem(data.hero.performance_rm, 80, 60)} />
            <KpiCard icon={BedDouble} label="Produttività HK" value={data.hero.hk_produttivita} unit="cam/h" semaforo={sem(data.hero.hk_produttivita, 2.4, 2.0)} />
          </div>

          {/* Classifica */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="ih-glass rounded-2xl p-6" data-testid="top-hotels">
              <div className="flex items-center gap-2 mb-4"><Trophy className="h-4 w-4 text-yellow-500" /><h2 className="font-display text-lg tracking-tight">Top hotel del mese</h2></div>
              <div className="space-y-2">
                {data.top.map((h, i) => (
                  <div key={h.id} onClick={() => navigate(`/strutture/${h.id}`)} data-testid={`top-${i}`} className="rounded-xl border border-border/60 p-3 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-3">
                    <span className="text-2xl">{["🥇", "🥈", "🥉"][i]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{h.nome}</div>
                      <div className="text-xs text-muted-foreground">{h.citta}</div>
                    </div>
                    <span className="font-display text-2xl">{h.punteggio}<span className="text-xs text-muted-foreground">/100</span></span>
                  </div>
                ))}
              </div>
            </div>
            <RelazioniFeedWidget />
          </div>

          {/* Hotel da attenzionare */}
          <div className="ih-glass rounded-2xl p-6" data-testid="bottom-hotels">
              <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-4 w-4 text-rose-500" /><h2 className="font-display text-lg tracking-tight">Hotel da attenzionare</h2></div>
              {data.bottom.length === 0 ? <p className="text-sm text-muted-foreground">Tutti gli hotel sopra soglia 🎉</p> : (
                <div className="space-y-2">
                  {data.bottom.map((h) => (
                    <div key={h.id} onClick={() => navigate(`/strutture/${h.id}`)} className="rounded-xl border border-rose-500/30 p-3 bg-rose-50/40 dark:bg-rose-900/10 hover:bg-rose-100/30 cursor-pointer flex items-center gap-3">
                      <AlertCircle className="h-4 w-4 text-rose-500" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{h.nome}</div>
                        <div className="text-xs text-muted-foreground">{h.citta}</div>
                      </div>
                      <span className="font-display text-2xl text-rose-600 dark:text-rose-400">{h.punteggio}<span className="text-xs text-muted-foreground">/100</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          {/* Operatività */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="operatività">
            <div className="ih-glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Controlli ultimi 7gg</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl font-medium text-emerald-600 dark:text-emerald-400">{data.operativita.completati_7}</span>
                <span className="text-xs text-muted-foreground">su {data.operativita.tot_7}</span>
              </div>
              <div className="h-10 mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.operativita.daily}>
                    <Bar dataKey="value" fill="#a87fd6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="ih-glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="h-3 w-3 text-amber-500" /> Non eseguiti / Ritardo</div>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-3xl font-medium text-amber-600">{data.operativita.non_eseguiti_7}</span>
                <span className="text-xs text-muted-foreground">non eseguiti</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">+ {data.operativita.in_ritardo_7} in ritardo</div>
            </div>
            <div onClick={() => navigate("/alert?priorita=critica")} className="ih-glass rounded-2xl p-5 ih-card-hover cursor-pointer">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-rose-500" /> Alert critici attivi</div>
              <span className="font-display text-3xl font-medium text-rose-600 dark:text-rose-400">{data.alert_critici_attivi}</span>
              <div className="text-[11px] text-primary mt-1">Vedi tutti →</div>
            </div>
          </div>

          {/* AI Insight */}
          <AIInsightCard endpoint="/ai/anomaly-narrative" params={{ periodo: 30 }} title="Cosa richiede attenzione adesso" sections={[]} />

          {/* v102 — Avanzamento Settimanale Controllo Camere */}
          <WeeklyRoomProgressWidget />

          {/* Controlli Extra Weekend (visibile solo se >0) */}
          {data.weekend_extra && (data.weekend_extra.controlli_totali || 0) > 0 && (
            <WeekendExtraWidget we={data.weekend_extra} />
          )}

          {/* Trend */}
          {data.trend.length > 0 && (
            <div className="ih-glass rounded-2xl p-6">
              <h2 className="font-display text-lg tracking-tight mb-4 flex items-center gap-2"><Activity className="h-4 w-4" /> Trend qualità corporate · {periodo}gg</h2>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="data" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis fontSize={10} domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="qualita" stroke="#a87fd6" strokeWidth={2.5} dot={false} name="Qualità media" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Andamento anno corrente (rolling 12 mesi) */}
          <YearlyTrendWidget user={user} />
        </>
      )}
    </div>
  );
}

function WeeklyRoomProgressWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/dashboard/weekly-room-progress")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) {
    return (
      <div className="ih-glass rounded-2xl p-5 flex items-center gap-3 text-sm text-muted-foreground" data-testid="weekly-room-progress-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Avanzamento settimanale camere…
      </div>
    );
  }
  if (!data || !data.hotels || data.hotels.length === 0) return null;
  const avg = data.corporate_avg_pct ?? 0;
  const avgCls = avg >= 90 ? "text-emerald-600 dark:text-emerald-400" : avg >= 70 ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400";
  const openCamere = async (hotelId) => {
    try {
      const { data: checks } = await api.get("/checks", { params: { hotel_id: hotelId, stato: "da_fare" } });
      const camereCheck = (checks || []).find((c) => c.categoria === "camere");
      if (camereCheck) {
        navigate(`/controlli/${camereCheck.id}`);
      } else {
        navigate("/controlli");
      }
    } catch { navigate("/controlli"); }
  };
  const fmtRange = (from, to) => {
    const f = new Date(from), t = new Date(to);
    const m = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];
    return `${f.getDate()}–${t.getDate()} ${m[t.getMonth()]}`;
  };
  return (
    <div className="ih-glass rounded-2xl p-5 border-l-4 border-l-lavender-400" data-testid="weekly-room-progress-widget">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-700 dark:text-lavender-300 mb-1 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> Avanzamento Settimanale · Controllo Camere
          </div>
          <h3 className="font-display text-lg tracking-tight">
            Settimana {fmtRange(data.week_from, data.week_to)}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-muted-foreground">Media corporate</div>
          <div className={`font-display text-3xl font-semibold tabular-nums ${avgCls}`} data-testid="weekly-corporate-avg">{avg}%</div>
        </div>
      </div>
      <div className="space-y-2">
        {data.hotels.map((h) => {
          const barCls = h.status === "green" ? "bg-emerald-500" : h.status === "amber" ? "bg-amber-500" : "bg-rose-500";
          const pctCls = h.status === "green" ? "text-emerald-700 dark:text-emerald-300" : h.status === "amber" ? "text-amber-700 dark:text-amber-300" : "text-rose-700 dark:text-rose-300";
          return (
            <button
              key={h.hotel_id}
              type="button"
              onClick={() => openCamere(h.hotel_id)}
              data-testid={`weekly-progress-row-${h.hotel_id}`}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent/40 transition-colors text-left group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-medium truncate group-hover:text-lavender-700 dark:group-hover:text-lavender-300 transition-colors">
                    {h.hotel_name}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-2 flex-shrink-0">
                    {/* v103 — Sparkline mini-trend 7gg (SVG puro, performance) */}
                    {Array.isArray(h.trend_7d) && h.trend_7d.length === 7 && (
                      <svg
                        width="60" height="20" viewBox="0 0 60 20"
                        data-testid={`weekly-progress-sparkline-${h.hotel_id}`}
                        aria-hidden="true"
                      >
                        {(() => {
                          const pts = h.trend_7d.map((v, i) => {
                            const x = (i / 6) * 60;
                            const y = 18 - (Math.min(100, Math.max(0, v)) / 100) * 16;
                            return [x, y];
                          });
                          const d = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
                          const last = pts[pts.length - 1];
                          return (
                            <>
                              <path d={d} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx={last[0]} cy={last[1]} r="2.5" fill="#8b5cf6" />
                            </>
                          );
                        })()}
                      </svg>
                    )}
                    <span>
                      <span className="font-semibold text-foreground tabular-nums">{h.controllate_sett}</span>
                      <span className="text-muted-foreground">/{h.obiettivo_sett}</span>
                      <span className={`ml-2 font-semibold tabular-nums ${pctCls}`}>{h.pct}%</span>
                    </span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-black/5 dark:bg-white/5 overflow-hidden">
                  <div className={`h-full transition-all ${barCls}`} style={{ width: `${Math.min(100, h.pct)}%` }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function WeekendExtraWidget({ we }) {
  const [open, setOpen] = useState(false);
  const totali = we.controlli_totali || 0;
  const completati = we.controlli_completati || 0;
  const classifica = we.rm_classifica || [];
  return (
    <div
      className="ih-glass rounded-2xl p-5 border-l-4 border-l-amber-400 relative overflow-hidden"
      data-testid="weekend-extra-widget"
      style={{ backgroundImage: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, transparent 55%)" }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
          <Star className="h-5 w-5 text-amber-500" fill="currentColor" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300 mb-1">
            Controlli Extra Weekend
          </div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-3xl font-medium tabular-nums" data-testid="we-totali">{totali}</span>
            <span className="text-sm text-muted-foreground">controlli · {we.giorni_coperti || 0} giorni</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <span className="tabular-nums">{completati}</span> completati
            {we.punteggio_medio != null && (
              <> · punteggio medio <span className="font-medium text-foreground tabular-nums">{we.punteggio_medio}/100</span></>
            )}
          </div>
        </div>
        {classifica.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="we-toggle-detail"
            className="text-xs text-amber-700 dark:text-amber-300 hover:underline font-medium"
          >
            {open ? "Nascondi dettaglio" : "Vedi dettaglio"} →
          </button>
        )}
      </div>
      {open && classifica.length > 0 && (
        <div className="mt-4 pt-4 border-t border-amber-200/40 dark:border-amber-900/40" data-testid="we-detail">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Top RM che fanno weekend
          </div>
          <div className="space-y-1.5">
            {classifica.map((r) => (
              <div
                key={r.user_id}
                className="flex items-center justify-between text-sm rounded-lg px-3 py-2 bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/40 dark:border-amber-900/40"
              >
                <span className="truncate">{r.user_name}</span>
                <span className="font-medium tabular-nums text-amber-700 dark:text-amber-300">{r.count} ctrl</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function YearlyTrendWidget({ user }) {
  const isCA = user?.role === "capo_area";
  const isSA = user?.role === "super_admin";
  const [scope, setScope] = useState(isSA || isCA ? "corporate" : "rm");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/performance/trend?scope=${scope}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [scope]);

  const points = data?.points || [];
  const showToggle = isSA || isCA;
  const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  const fmtMonth = (m) => {
    if (!m) return "";
    const [y, mm] = m.split("-");
    return `${MONTHS_IT[parseInt(mm, 10) - 1] || mm} '${y.slice(2)}`;
  };

  return (
    <div className="ih-glass rounded-2xl p-6" data-testid="yearly-trend-widget">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-display text-lg tracking-tight flex items-center gap-2">
          <Activity className="h-4 w-4" /> Andamento anno corrente
        </h2>
        {showToggle && (
          <div className="flex gap-1 ih-glass rounded-full p-1" data-testid="yearly-scope-toggle">
            {[
              { v: "corporate", label: isSA ? "Corporate" : "Miei hotel" },
              { v: "rm", label: "Personale" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`yearly-scope-${o.v}`}
                onClick={() => setScope(o.v)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${scope === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        <Skeleton className="h-[240px]" />
      ) : points.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-10" data-testid="yearly-empty">
          Nessun dato disponibile per il periodo selezionato.
        </div>
      ) : (
        <>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points.map((p) => ({ ...p, label: fmtMonth(p.month) }))}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} domain={[0, 100]} />
                <Tooltip formatter={(v, n) => n === "punteggio_medio" ? [`${v}/100`, "Punteggio medio"] : [v, n]} />
                <Line type="monotone" dataKey="punteggio_medio" stroke="#a87fd6" strokeWidth={2.5} dot={{ r: 3 }} name="Punteggio medio" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-muted-foreground" data-testid="yearly-stats">
            <span>
              <span className="font-medium text-foreground">Media anno: </span>
              <span className="tabular-nums">{data.current_avg ?? "—"}</span>
              {data.current_avg != null ? "/100" : ""}
            </span>
            {data.delta_vs_previous != null && (
              <span
                className={`inline-flex items-center gap-1 ${data.delta_vs_previous >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                data-testid="yearly-delta"
              >
                {data.delta_vs_previous >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {data.delta_vs_previous >= 0 ? "+" : ""}{data.delta_vs_previous} vs anno precedente
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider">
              {points.length} {points.length === 1 ? "mese" : "mesi"} · scoring 100/85/65/40/0
            </span>
          </div>
        </>
      )}
    </div>
  );
}


function OperationalDashboard({ user }) {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  useEffect(() => { api.get("/hotels").then((r) => setHotels(r.data || [])); }, []);

  return (
    <div className="space-y-6 ih-anim-in" data-testid="operational-dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Operatività</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Ciao, <span className="font-medium">{user.nome}</span></h1>
        <p className="text-sm text-muted-foreground mt-1">Ecco i tuoi accessi rapidi.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { to: "/controlli", icon: ClipboardList, label: "Controlli giornalieri", desc: "Esegui i tuoi controlli di oggi" },
          { to: "/strutture", icon: Building2, label: "Le tue strutture", desc: `${hotels.length} hotel assegnati` },
          { to: "/staff", icon: Users, label: "Staff", desc: "Gestione team e valutazioni" },
          { to: "/notifiche", icon: AlertTriangle, label: "Notifiche", desc: "Centro avvisi" },
          { to: "/report", icon: TrendingUp, label: "Report", desc: "Esporta PDF/Excel" },
        ].map((c) => (
          <button key={c.to} onClick={() => navigate(c.to)} className="ih-glass rounded-2xl p-5 ih-card-hover text-left">
            <c.icon className="h-6 w-6 text-lavender-500 mb-2" />
            <div className="font-display text-lg tracking-tight">{c.label}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
